
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { EllipsisVerticalIcon } from "@heroicons/vue/24/outline";
import { TrashIcon } from "@heroicons/vue/24/solid";
import { PlannerComment } from "@oculai/planner";
import { parseISO } from "date-fns";
import TimeAgo from "javascript-time-ago";
import { defineComponent } from "vue";
import LoadingSpinner from "@/components/loading_state/LoadingSpinner.vue";
import { useConfirmationModal } from "@/composables/toast";
import PlannerRepository from "@/repositories/PlannerRepository";
import logger from "@/services/logger";

export default defineComponent({
  name: "PlannerComments",
  props: {
    sourceId: {
      type: String,
      required: true,
    },
  },
  components: {
    EllipsisVerticalIcon,
    LoadingSpinner,
    TrashIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
  },
  mounted() {
    this.load();
  },
  emits: ["commentAdded", "commentDeleted"],
  data() {
    return {
      comments: [] as PlannerComment[],
      comment: "" as string,
      loading: false,
      creatingComment: false,
    };
  },
  computed: {
    isCommentButtonEnabled() {
      return !this.creatingComment && this.comment;
    },
    email() {
      return this.$store.state.user.email;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.comments = await PlannerRepository.loadPlannerComments(
          this.currentCustomerName,
          this.currentSiteId,
          this.sourceId,
        );
      } catch (error) {
        logger.error("Unable to load comments", error as Error);
      } finally {
        this.loading = false;
      }
    },
    parseDate(dateString: string) {
      return parseISO(dateString);
    },
    formatInTimeAgo(dateString: string) {
      const timeAgo = new TimeAgo(this.$i18n.locale);
      const date = this.parseDate(dateString);
      // creation date should never lay in the future
      const finalDate = date < new Date() ? date : new Date();

      return timeAgo.format(finalDate);
    },
    async createComment() {
      try {
        this.creatingComment = true;
        const newComment = await PlannerRepository.createComment(
          this.currentCustomerName,
          this.currentSiteId,
          this.comment,
          this.sourceId,
        );
        this.comments.push(newComment);
        this.comment = "";
        this.$emit("commentAdded", newComment);
      } catch (error) {
        logger.error("Unable to create comment", error as Error);
        alert(this.$t("analytics.planner.comments.unable_to_create_error"));
      } finally {
        this.creatingComment = false;
      }
    },
    async handleDeleteClick(comment: PlannerComment) {
      const isDeletionConfirmed = await this.showConfirmationModal({
        color: "red",
        header: this.$t("analytics.planner.comments.delete_confirmation_title"),
        confirmAction: this.$t("analytics.planner.comments.delete_confirmation_confirm"),
        cancelAction: this.$t("analytics.planner.comments.delete_confirmation_cancel"),
      });

      if (!isDeletionConfirmed || !comment) {
        return;
      }

      const { customer_name, site_id } = this.$route.params;

      try {
        await PlannerRepository.deleteComment(
          customer_name as string,
          site_id as string,
          comment._id,
        );
        this.comments.splice(this.comments.indexOf(comment), 1);
        this.$emit("commentDeleted", comment);
      } catch (error) {
        logger.error("Unable to delete comment", error as Error);
        alert(this.$t("analytics.planner.comments.unable_to_delete_error"));
      }
    },
  },

  setup() {
    const showConfirmationModal = useConfirmationModal();

    return {
      showConfirmationModal,
    };
  },
});
